import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { LayoutHeader } from "@/Header";
import { ValidateInput } from "@/Input";
import MessageModal from "@/MessageModal";
import {
  useBusinessMypageSearchUser,
  useBusinessMypageTicketList,
  useIssueManualTicket,
  resolveMemberCode,
} from "~/services/business/myPage";
import QrScannerModal from "@/common/QrScannerModal";
import { useBusinessAuthUserInfo } from "~/services/business/auth";
import {
  checkActionCost,
  type ActionCostCheck,
} from "~/services/business/credit";
import { isMungApp, openExternalBrowser } from "%/externalBrowser";

export const Route = createFileRoute(
  "/business/layout/user-subscription-issue",
)({
  component: SubscriptionIssue,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      productTicketSeq: search.productTicketSeq
        ? Number(search.productTicketSeq)
        : undefined,
      serviceType: (search.serviceType as string) || undefined,
    };
  },
});

interface SearchUserItem {
  dtUpdate: string;
  delYn: string;
  dtRegist: string;
  userSeq: number;
  petSeq: number;
  petName: string;
  cdUserType: string;
  email: string;
  snsId: string;
  cdSnsType: string;
  userName: string;
  nickname: string;
  phoneNumber: string;
  alarmNoticeYn: string;
  alarmMarketingYn: string;
  alarmSmsYn: string;
  alarmEmailYn: string;
}

/** 통합 검색 결과 1행 — 코드/전화번호/펫이름 조회를 하나로 합친 표현. */
interface UnifiedRow {
  userSeq: number;
  userName: string;
  phoneNumber: string;
  petSeq: number;
  petName: string;
  /** 이 사업장의 기존 이용고객 여부 (예약 이력 존재) */
  existingCustomer: boolean;
}

interface PickableTicket {
  productTicketSeq: number;
  name: string;
  activeYn?: string;
  dtExpired?: string | null;
}

function SubscriptionIssue() {
  const navigate = useNavigate();
  const { productTicketSeq } = useSearch({
    from: "/business/layout/user-subscription-issue",
  });

  // 이용권 미지정 진입(홈 바로가기 등) 시 화면에서 직접 선택
  const [pickedTicket, setPickedTicket] = useState<{
    seq: number;
    name: string;
  } | null>(null);
  const effectiveTicketSeq = productTicketSeq ?? pickedTicket?.seq;

  const { data: ticketListData } = useBusinessMypageTicketList();
  const pickableGroups = useMemo(() => {
    if (productTicketSeq || !ticketListData) return [];
    const groups: Array<{ label: string; items: PickableTicket[] }> = [
      { label: "유치원", items: ticketListData.kindergarten ?? [] },
      { label: "호텔", items: ticketListData.hotel ?? [] },
      { label: "데이케어", items: ticketListData.daycare ?? [] },
      { label: "미용", items: ticketListData.grooming ?? [] },
    ];
    // 발급 가능 판정 = 활성(activeYn='Y') + 유효기간 미경과.
    // 관리 화면은 사업자 관점의 "활성 설정" 여부만 보여주고,
    // 발급 모달은 실제로 발급해서 사용 가능한지(만료되지 않았는지) 를 함께 검사한다 —
    // 만료된 이용권을 발급하면 즉시 사용 불가 상태로 지급되는 문제 방지.
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (t: PickableTicket) =>
            t.activeYn === "Y" &&
            (!t.dtExpired || new Date(t.dtExpired).getTime() > Date.now()),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [productTicketSeq, ticketListData]);

  const [searchType, setSearchType] = useState<"PHONENUMBER" | "PETNAME">(
    "PHONENUMBER",
  );
  const [currentSearchKeyword, setCurrentSearchKeyword] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<SearchUserItem | null>(null);
  // 통합 검색 (코드/전화번호/펫이름 한 입력창 + QR)
  const [searchInput, setSearchInput] = useState<string>("");
  const [results, setResults] = useState<UnifiedRow[]>([]);
  const [searchError, setSearchError] = useState<string>("");
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [scannerOpen, setScannerOpen] = useState<boolean>(false);
  // 조회는 됐지만 이 사업장과 계약(서명)이 안 된 펫 — 계약서 발송 안내(그 펫을 물고 발송화면으로)
  const [notContracted, setNotContracted] = useState<{
    userSeq: number;
    petSeq: number;
    userName: string;
    petName: string;
    phoneNumber: string;
  } | null>(null);
  const [qty, setQty] = useState<string>("1");
  const [isIssuing, setIsIssuing] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  // 크레딧 차감 안내 모달
  const [costCheck, setCostCheck] = useState<ActionCostCheck | null>(null);
  const [isCostConfirmOpen, setIsCostConfirmOpen] = useState(false);
  const [isInsufficientOpen, setIsInsufficientOpen] = useState(false);

  // 권한 정보 — 선생님은 크레딧 충전 불가라 잔액 부족 시 충전 이동 버튼 숨김
  const { data: authUserInfo } = useBusinessAuthUserInfo();
  const isTeacher = authUserInfo?.isTeacher ?? false;

  // 이용권 발급 API
  const issueTicketMutation = useIssueManualTicket();

  // API 호출
  const {
    data: searchUserData,
    isLoading,
    refetch,
  } = useBusinessMypageSearchUser({
    searchType,
    keyword: currentSearchKeyword,
  });

  // 검색어에서 8자리 회원코드만 뽑아낸다. MY코드 = 0/O·1/I/L 제외 대문자 8자리.
  const CODE_RE = /^[2-9A-HJ-NP-Z]{8}$/;
  const extractCode = (raw: string): string => {
    const t = raw.trim().toUpperCase();
    if (t.startsWith("MTKT:")) return (t.split(":")[1] ?? "").trim();
    if (CODE_RE.test(t)) return t;
    return "";
  };

  // 검색 결과(전화/펫이름) → 통합 행
  const mapSearchRows = (items: SearchUserItem[]): UnifiedRow[] =>
    items.map((u) => ({
      userSeq: u.userSeq,
      userName: u.userName,
      phoneNumber: u.phoneNumber,
      petSeq: u.petSeq,
      petName: u.petName,
      existingCustomer: Boolean(
        (u as unknown as { existingCustomer?: boolean }).existingCustomer,
      ),
    }));

  // 전화/펫이름 검색 결과가 도착하면 통합 결과로 반영 (코드 조회는 별도로 직접 setResults)
  useEffect(() => {
    if (!currentSearchKeyword) return;
    if (!searchUserData?.data) return;
    // 보호자의 펫을 전부 노출(계약완료/미계약 뱃지). 미계약 펫은 selectRow 에서 '계약 먼저' 유도.
    const rows = mapSearchRows(searchUserData.data as SearchUserItem[]);
    if (rows.length > 0) {
      setResults(rows);
      setSearchError("");
    } else {
      setResults([]);
      setSearchError("검색 결과가 없습니다.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUserData]);

  // 통합 검색 — 코드/QR → 회원조회, 숫자 → 전화번호, 그 외 → 펫이름
  const runUnifiedSearch = async (raw: string) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) return;
    setSelectedUser(null);
    setSearchError("");
    setNotContracted(null);

    const code = extractCode(trimmed);
    if (code) {
      setResults([]);
      setSearchLoading(true);
      try {
        const member = await resolveMemberCode(code);
        if (!member) {
          // 코드 테이블에 없음
          setSearchError("없는 코드입니다.");
          return;
        }
        // 보호자의 펫을 전부 노출 — 계약완료/미계약 뱃지로 구분해 선택하게 한다.
        // (미계약 펫 선택 시 발급 대신 '계약 먼저 발송' 유도 — selectRow 에서 게이트)
        if (member.pets.length === 0) {
          setSearchError("등록된 반려동물이 없습니다.");
          return;
        }
        setResults(
          member.pets.map((p) => ({
            userSeq: member.userSeq,
            userName: member.userName,
            phoneNumber: member.phoneNumber,
            petSeq: p.petSeq,
            petName: p.petName,
            existingCustomer: Boolean(p.existingCustomer),
          })),
        );
      } catch {
        // 404 = 코드 없음
        setSearchError("없는 코드입니다.");
      } finally {
        setSearchLoading(false);
      }
      return;
    }

    // 숫자·하이픈·공백만 → 전화번호(하이픈 제거 후 조회), 그 외 → 펫이름
    const isPhone = /^[0-9\-\s]+$/.test(trimmed);
    const type: "PHONENUMBER" | "PETNAME" = isPhone ? "PHONENUMBER" : "PETNAME";
    const keyword = isPhone ? trimmed.replace(/[^0-9]/g, "") : trimmed;
    setResults([]);
    if (type === searchType && keyword === currentSearchKeyword) {
      // 같은 조건 재검색 — 훅 키가 안 바뀌므로 수동 refetch
      refetch();
    } else {
      setSearchType(type);
      setCurrentSearchKeyword(keyword);
    }
  };

  // 통합 행 → 발급 대상 확정
  const applyRow = (row: UnifiedRow) => {
    setSelectedUser({
      userSeq: row.userSeq,
      petSeq: row.petSeq,
      petName: row.petName,
      userName: row.userName,
      phoneNumber: row.phoneNumber,
    } as SearchUserItem);
  };

  // 행 선택 — 계약완료 펫은 발급 대상 확정, 미계약 펫은 '계약 먼저 발송' 유도
  const selectRow = (row: UnifiedRow) => {
    if (!row.existingCustomer) {
      setSelectedUser(null);
      setNotContracted({
        userSeq: row.userSeq,
        petSeq: row.petSeq,
        userName: row.userName,
        petName: row.petName,
        phoneNumber: row.phoneNumber,
      });
      return;
    }
    applyRow(row);
  };

  // 1단계: 발급 클릭 → 크레딧 프리체크 → 분기 (충분/부족)
  const handleIssue = async () => {
    if (!selectedUser || !effectiveTicketSeq || !qty) {
      return;
    }

    const qtyNumber = Number.parseInt(qty, 10);
    if (qtyNumber <= 0) {
      alert("수량은 1 이상이어야 합니다.");
      return;
    }

    setIsIssuing(true);
    try {
      // 크레딧 차감 비용 + 잔액 프리체크 — 이용권 seq 전달 시 실차감과 동일한 KRW 환산으로 계산
      const check = await checkActionCost(
        "USE_TICKET_OFFLINE",
        qtyNumber,
        effectiveTicketSeq,
      );
      setCostCheck(check);
      setIsIssuing(false);

      if (!check.sufficient) {
        // 잔액 부족 → 충전 안내 모달
        setIsInsufficientOpen(true);
      } else {
        // 충분 → 차감 안내 확인 모달
        setIsCostConfirmOpen(true);
      }
    } catch (error: any) {
      console.error("크레딧 프리체크 실패:", error);
      // precheck 실패해도 발급 자체는 시도 가능 — 기존 동작 유지
      setIsIssuing(false);
      await performIssue(qtyNumber);
    }
  };

  // 2단계: 실제 발급 호출
  const performIssue = async (qtyNumber: number) => {
    if (!selectedUser || !effectiveTicketSeq) return;
    setIsIssuing(true);
    try {
      const response = await issueTicketMutation.mutateAsync({
        userSeq: selectedUser.userSeq,
        petSeq: selectedUser.petSeq,
        productTicketSeq: effectiveTicketSeq,
        qty: qtyNumber,
      });

      if (response.statusCode === 200 || response.statusCode === 201) {
        setSuccessMessage(
          response.message || "이용권이 성공적으로 발급되었습니다.",
        );
        setIsSuccessModalOpen(true);
      } else {
        // BE 가 INSUFFICIENT_CREDIT|현재|필요 형식으로 던질 수 있음
        const msg = response.message || "이용권 발급에 실패했습니다.";
        if (msg.includes("INSUFFICIENT_CREDIT")) {
          setIsInsufficientOpen(true);
        } else {
          setErrorMessage(msg);
          setIsErrorModalOpen(true);
        }
      }
    } catch (error: any) {
      console.error("이용권 발급 실패:", error);
      const rawMsg =
        error?.response?.data?.message ||
        error?.message ||
        "이용권 발급에 실패했습니다. 다시 시도해주세요.";
      if (String(rawMsg).includes("INSUFFICIENT_CREDIT")) {
        setIsInsufficientOpen(true);
      } else {
        setErrorMessage(rawMsg);
        setIsErrorModalOpen(true);
      }
    } finally {
      setIsIssuing(false);
    }
  };

  // 통합 결과 1행 렌더 — 보호자명·전화번호·펫이름 + 이용고객 배지
  const renderRow = (row: UnifiedRow) => {
    const isSelected =
      selectedUser?.userSeq === row.userSeq &&
      selectedUser?.petSeq === row.petSeq;
    return (
      <div
        key={`${row.userSeq}-${row.petSeq}`}
        onClick={() => selectRow(row)}
        className={`cursor-pointer rounded-lg border-2 bg-white p-4 transition-colors ${
          isSelected
            ? "border-orange-400 bg-orange-50"
            : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold text-gray-900">
              {row.petName || "이름 없음"}
            </div>
            <div className="flex items-center gap-1.5">
              {row.existingCustomer ? (
                <span className="rounded bg-[#EAF7EE] px-2 py-0.5 text-[11px] font-semibold text-[#22A55B]">
                  계약완료
                </span>
              ) : (
                <span className="rounded bg-[#FFF1DE] px-2 py-0.5 text-[11px] font-semibold text-[#E67E00]">
                  미계약
                </span>
              )}
              {isSelected && (
                <span className="rounded bg-orange-100 px-2 py-0.5 text-[11px] font-medium text-orange-500">
                  선택됨
                </span>
              )}
            </div>
          </div>
          <div className="text-sm text-gray-600">
            보호자: {row.userName || "-"}
          </div>
          <div className="text-sm text-gray-600">
            전화번호: {row.phoneNumber || "-"}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <LayoutHeader title="이용권 발급" hasBackBtn hasFootPrint={false} />
      <div className="flex-auto flex flex-col bg-[#F5F5F5]">
        <div className="flex flex-col gap-4 p-4">
          {/* 발급할 이용권 선택 — 이용권 미지정 진입(홈 바로가기) 시에만 노출 */}
          {!productTicketSeq && (
            <div className="flex flex-col gap-2">
              <div className="text-sm font-semibold text-gray-700">
                발급할 이용권
              </div>
              {pickedTicket ? (
                <div className="flex items-center justify-between rounded-lg border border-orange-400 bg-white px-4 py-3">
                  <span className="text-sm font-semibold">
                    {pickedTicket.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPickedTicket(null)}
                    className="shrink-0 text-xs text-gray-500 underline"
                  >
                    변경
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3 rounded-lg bg-white p-3">
                  {pickableGroups.length === 0 && (
                    <div className="py-4 text-center text-sm text-gray-400">
                      발급 가능한 이용권이 없습니다.
                      <br />
                      이용권 관리에서 먼저 등록해주세요.
                    </div>
                  )}
                  {pickableGroups.map((group) => (
                    <div key={group.label}>
                      <div className="mb-1.5 text-xs font-semibold text-gray-500">
                        {group.label}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {group.items.map((t) => (
                          <button
                            key={t.productTicketSeq}
                            type="button"
                            onClick={() =>
                              setPickedTicket({
                                seq: t.productTicketSeq,
                                name: t.name,
                              })
                            }
                            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm transition-colors hover:border-orange-400"
                          >
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 통합 검색 — 회원코드 / 전화번호 / 반려동물 이름 + QR */}
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <div className="mb-2 text-[13px] font-semibold text-gray-800">
              고객 찾기
              <span className="ml-1 text-[11px] font-normal text-gray-400">
                회원코드 · 전화번호 · 반려동물 이름
              </span>
            </div>
            <div className="flex gap-2">
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") runUnifiedSearch(searchInput);
                }}
                placeholder="회원코드·전화번호·이름 입력"
                className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-[14px] focus:border-[#FF9934] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => runUnifiedSearch(searchInput)}
                className="shrink-0 rounded-lg bg-[#FF9934] px-3 py-2 text-[13px] font-semibold text-white"
              >
                조회
              </button>
              <button
                type="button"
                onClick={() => setScannerOpen(true)}
                className="shrink-0 rounded-lg border border-[#FF9934] px-3 py-2 text-[13px] font-semibold text-[#FF9934]"
              >
                📷 QR
              </button>
            </div>
          </div>

          {/* 검색 상태 */}
          {(searchLoading || isLoading) && (
            <div className="flex items-center justify-center py-10">
              <div className="text-gray-500">검색 중...</div>
            </div>
          )}

          {!searchLoading && !isLoading && searchError && (
            <div className="flex items-center justify-center py-10">
              <div className="text-gray-500">{searchError}</div>
            </div>
          )}

          {/* 검색 결과 — 보호자의 펫 전체(계약완료/미계약 뱃지). 미계약 선택 시 계약 먼저 발송 유도 */}
          {!searchLoading && !isLoading && results.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="px-1 text-xs font-semibold text-[#666]">
                검색 결과 ({results.length}) · 펫을 선택하세요
              </div>
              {results.map((row) => renderRow(row))}
            </div>
          )}

          {/* 수량 입력 */}
          {selectedUser && (
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <label
                htmlFor="qty"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                발급 수량 *
              </label>
              <ValidateInput
                id="qty"
                type="number"
                value={qty}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d+$/.test(value)) {
                    setQty(value);
                  }
                }}
                placeholder="수량을 입력하세요"
                min="1"
                className="w-full"
              />
              <div className="mt-1 text-xs text-gray-500">
                1 이상의 숫자를 입력해주세요.
              </div>
            </div>
          )}

          {/* 발급 버튼 */}
          <div className="flex gap-3 pb-4 mt-4">
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: "/business/layout/user-subscription-management",
                })
              }
              className="flex-1 py-3 text-base font-semibold text-gray-600 bg-white rounded-lg border border-gray-300"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleIssue}
              disabled={
                !selectedUser ||
                !effectiveTicketSeq ||
                !qty ||
                Number.parseInt(qty, 10) <= 0 ||
                isIssuing
              }
              className="flex-1 py-3 text-base font-semibold text-white bg-orange-400 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isIssuing ? "발급 중..." : "발급하기"}
            </button>
          </div>
        </div>
      </div>

      {/* 성공 모달 */}
      <MessageModal
        isOpen={isSuccessModalOpen}
        onRequestClose={() => {
          setIsSuccessModalOpen(false);
          navigate({
            to: "/business/layout/home",
          });
        }}
        className="pt-6 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
        title="이용권 발급 완료"
      >
        <div className="flex flex-col gap-4 items-center">
          <div className="text-center text-gray-700">
            {successMessage.split("\n").map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
          <button
            onClick={() => {
              setIsSuccessModalOpen(false);
              navigate({
                to: "/business/layout/home",
              });
            }}
            className="py-3 w-full text-base font-semibold text-white bg-orange-400 rounded-lg"
          >
            확인
          </button>
        </div>
      </MessageModal>

      {/* 실패 모달 */}
      <MessageModal
        isOpen={isErrorModalOpen}
        onRequestClose={() => setIsErrorModalOpen(false)}
        className="pt-6 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
        title="이용권 발급 실패"
      >
        <div className="flex flex-col gap-4 items-center">
          <div className="text-center text-gray-700">{errorMessage}</div>
          <button
            onClick={() => setIsErrorModalOpen(false)}
            className="py-3 w-full text-base font-semibold text-white bg-orange-400 rounded-lg"
          >
            확인
          </button>
        </div>
      </MessageModal>

      {/* 크레딧 차감 안내 (잔액 충분) */}
      <MessageModal
        isOpen={isCostConfirmOpen}
        onRequestClose={() => setIsCostConfirmOpen(false)}
        className="pt-6 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
        title="크레딧 차감 확인"
      >
        <div className="flex flex-col gap-4 items-center">
          <div className="text-center text-gray-700 space-y-1">
            {costCheck?.exempt ? (
              <>
                <div>🛡 차감 면제 기간이 적용 중입니다.</div>
                <div className="text-sm text-gray-500">
                  발급 시 크레딧이 차감되지 않습니다.
                </div>
              </>
            ) : (
              <div className="text-base">
                이용권 발급 시{" "}
                <strong className="text-orange-500">
                  {costCheck?.totalCost ?? 0}
                </strong>{" "}
                크레딧이 차감됩니다.
              </div>
            )}
          </div>
          <div className="flex gap-2 w-full">
            <button
              onClick={() => setIsCostConfirmOpen(false)}
              className="flex-1 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-lg border border-gray-300"
            >
              취소
            </button>
            <button
              onClick={() => {
                setIsCostConfirmOpen(false);
                performIssue(costCheck?.qty ?? Number.parseInt(qty, 10));
              }}
              className="flex-1 py-3 text-base font-semibold text-white bg-orange-400 rounded-lg"
            >
              발급하기
            </button>
          </div>
        </div>
      </MessageModal>

      {/* 잔액 부족 안내 — 크레딧으로 이동 / 취소 */}
      <MessageModal
        isOpen={isInsufficientOpen}
        onRequestClose={() => setIsInsufficientOpen(false)}
        className="pt-6 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
        title="크레딧 잔액 부족"
      >
        <div className="flex flex-col gap-4 items-center">
          <div className="text-center text-gray-700 space-y-1">
            <div className="text-base">
              이용권 발급에 필요한 크레딧이 부족합니다.
            </div>
            {costCheck && (
              <div className="text-sm text-gray-500">
                현재 잔액 <strong>{costCheck.currentBalance}</strong> · 필요{" "}
                <strong>{costCheck.totalCost}</strong>
                {" · "}
                부족{" "}
                <strong className="text-red-500">
                  {costCheck.totalCost - costCheck.currentBalance}
                </strong>
              </div>
            )}
            {!isTeacher && (
              <div className="text-sm text-gray-500 pt-2">
                크레딧으로 이동하여 충전하시겠어요?
              </div>
            )}
          </div>
          <div className="flex gap-2 w-full">
            <button
              onClick={() => setIsInsufficientOpen(false)}
              className="flex-1 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-lg border border-gray-300"
            >
              취소
            </button>
            {!isTeacher && (
              <button
                onClick={() => {
                  setIsInsufficientOpen(false);
                  if (isMungApp()) {
                    openExternalBrowser("/business/layout/user-credit-wallet");
                  } else {
                    navigate({ to: "/business/layout/user-credit-wallet" });
                  }
                }}
                className="flex-1 py-3 text-base font-semibold text-white bg-orange-400 rounded-lg"
              >
                크레딧으로
              </button>
            )}
          </div>
        </div>
      </MessageModal>

      {/* 미계약 고객 안내 — 계약서 먼저 발송·서명 유도 */}
      <MessageModal
        isOpen={!!notContracted}
        onRequestClose={() => setNotContracted(null)}
        className="pt-6 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
        title="계약이 필요한 고객"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="space-y-1 text-center text-gray-700">
            <div className="text-base">
              아직{" "}
              <strong className="text-orange-500">계약이 완료되지 않은</strong>{" "}
              고객이에요.
            </div>
            <div className="text-sm text-gray-500">
              보호자 {notContracted?.userName || "-"} ·{" "}
              {notContracted?.phoneNumber || "-"}
            </div>
            <div className="pt-2 text-sm text-gray-500">
              이용권 발급 전에 <strong>계약서를 먼저 발송</strong>해 서명을
              받아주세요.
            </div>
          </div>
          <div className="flex w-full gap-2">
            <button
              onClick={() => setNotContracted(null)}
              className="flex-1 rounded-lg border border-gray-300 bg-gray-100 py-3 text-base font-semibold text-gray-700"
            >
              닫기
            </button>
            <button
              onClick={() => {
                const nc = notContracted;
                setNotContracted(null);
                // 그 미계약 펫을 물고 발송 화면으로 — 수신자·펫 미리 선택돼 재검색 불필요
                navigate({
                  to: "/business/layout/contract-request-send",
                  search: nc
                    ? {
                        userSeq: nc.userSeq,
                        petSeq: nc.petSeq,
                        userName: nc.userName,
                        petName: nc.petName,
                      }
                    : undefined,
                });
              }}
              className="flex-1 rounded-lg bg-orange-400 py-3 text-base font-semibold text-white"
            >
              계약서 발송
            </button>
          </div>
        </div>
      </MessageModal>

      {scannerOpen && (
        <QrScannerModal
          onClose={() => setScannerOpen(false)}
          onScan={(text) => {
            setScannerOpen(false);
            setSearchInput(text);
            runUnifiedSearch(text);
          }}
        />
      )}
    </>
  );
}
